import type { useI18n } from '@hive/i18n';
import { track } from '@hive/track';
import type { Workspace } from '@blocksuite/hive/store';
import { ArrowRightBigIcon } from '@blocksuite/icons/rc';
import type { createStore } from 'jotai';

import { openWorkspaceListModalAtom } from '../components/atoms';
import type { useNavigateHelper } from '../components/hooks/use-navigate-helper';
import type { WorkspaceDialogService } from '../modules/dialogs';
import { registerHiveCommand } from './registry';

export function registerHiveNavigationCommands({
  t,
  store,
  docCollection,
  navigationHelper,
  workspaceDialogService,
}: {
  t: ReturnType<typeof useI18n>;
  store: ReturnType<typeof createStore>;
  navigationHelper: ReturnType<typeof useNavigateHelper>;
  docCollection: Workspace;
  workspaceDialogService: WorkspaceDialogService;
}) {
  const unsubs: Array<() => void> = [];
  unsubs.push(
    registerHiveCommand({
      id: 'hive:goto-all-pages',
      category: 'hive:navigation',
      icon: <ArrowRightBigIcon />,
      label: t['com.hive.cmdk.hive.navigation.goto-all-pages'](),
      run() {
        track.$.cmdk.navigation.navigate({
          to: 'allDocs',
        });

        navigationHelper.jumpToPage(docCollection.id, 'all');
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: 'hive:goto-collection-list',
      category: 'hive:navigation',
      icon: <ArrowRightBigIcon />,
      label: 'Go to Collection List',
      run() {
        track.$.cmdk.navigation.navigate({
          to: 'collectionList',
        });

        navigationHelper.jumpToCollections(docCollection.id);
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: 'hive:goto-tag-list',
      category: 'hive:navigation',
      icon: <ArrowRightBigIcon />,
      label: 'Go to Tag List',
      run() {
        track.$.cmdk.navigation.navigate({
          to: 'tagList',
        });

        navigationHelper.jumpToTags(docCollection.id);
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: 'hive:goto-workspace',
      category: 'hive:navigation',
      icon: <ArrowRightBigIcon />,
      label: t['com.hive.cmdk.hive.navigation.goto-workspace'](),
      run() {
        track.$.cmdk.navigation.navigate({
          to: 'workspace',
        });

        store.set(openWorkspaceListModalAtom, true);
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: 'hive:open-settings',
      category: 'hive:navigation',
      icon: <ArrowRightBigIcon />,
      label: t['com.hive.cmdk.hive.navigation.open-settings'](),
      keyBinding: '$mod+,',
      run() {
        track.$.cmdk.settings.openSettings();
        workspaceDialogService.open('setting', {
          activeTab: 'appearance',
        });
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: 'hive:open-account',
      category: 'hive:navigation',
      icon: <ArrowRightBigIcon />,
      label: t['com.hive.cmdk.hive.navigation.open-account-settings'](),
      run() {
        track.$.cmdk.settings.openSettings({ to: 'account' });
        workspaceDialogService.open('setting', {
          activeTab: 'account',
        });
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: 'hive:goto-trash',
      category: 'hive:navigation',
      icon: <ArrowRightBigIcon />,
      label: t['com.hive.cmdk.hive.navigation.goto-trash'](),
      run() {
        track.$.cmdk.navigation.navigate({
          to: 'trash',
        });

        navigationHelper.jumpToPage(docCollection.id, 'trash');
      },
    })
  );

  return () => {
    unsubs.forEach(unsub => unsub());
  };
}
